import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../../applicationUi/components/Table";
import { format } from "date-fns";
import { fetchBlockTherapyReport } from "../../../../store/thunks/reportsThunk";
import * as XLSX from "xlsx";
import { toast } from 'react-toastify';

// Currency formatting utility
const formatCurrency = (amount) => {
  if (amount == null) return "$0.00";
  return `$${Number(amount).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
};

// Safe date formatting utility
const formatDate = (date, formatString = "MMM dd, yyyy") => {
  if (!date) return "-";
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return "-";
    }
    return format(dateObj, formatString);
  } catch (error) {
    console.error("Date formatting error:", error);
    return "-";
  }
};

// Status badge component
const StatusBadge = ({ status }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "bg-green-100 text-green-800";
      case "In Progress":
        return "bg-blue-100 text-blue-800";
      case "Active":
        return "bg-yellow-100 text-yellow-800";
      case "Cancelled":
        return "bg-red-100 text-red-800";
      case "Mixed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(status)}`}>
      {status}
    </span>
  );
};

// Progress bar component
const ProgressBar = ({ percentage }) => {
  return (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
        style={{ width: `${percentage}%` }}
      ></div>
    </div>
  );
};

const BlockTherapyReport = () => {
  const dispatch = useDispatch();
  const [selectedClinic, setSelectedClinic] = useState("all");
  const [selectedClinician, setSelectedClinician] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const {
    data: blockTherapyData,
    loading,
    error,
  } = useSelector((state) => state.reports.blockTherapy || { data: null, loading: false, error: null });

  const { data: clinics = [] } = useSelector((state) => state.clinic || { data: [] });
  const { data: clinicians = [] } = useSelector((state) => state.clinicians || { data: [] });

  const data = blockTherapyData?.data || {};
  const summary = data.summary || {};
  const blocks = data.blocks || [];

  // Load initial data on component mount
  useEffect(() => {
    dispatch(fetchBlockTherapyReport({ 
      clinicId: "all", 
      clinicianId: "all", 
      statusFilter: "all" 
    }));
  }, [dispatch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(fetchBlockTherapyReport({ 
      clinicId: selectedClinic, 
      clinicianId: selectedClinician, 
      statusFilter 
    }));
  };

  const handleDownload = () => {
    if (blocks.length === 0) {
      toast.warning("No data available to download.");
      return;
    }

    const data = blocks.map((block) => ({
      "Block ID": block._id,
      "Client Name": block.clientName,
      "Client Email": block.clientEmail,
      "Client Phone": block.clientPhone,
      "Client Status": block.clientStatus,
      "Clinic": block.clinicName,
      "Clinician": block.clinicianName,
      "Service": block.serviceName,
      "Service Price": formatCurrency(block.servicePrice),
      "Block Title": block.title,
      "Block Status": block.blockStatus,
      "Total Appointments": block.totalAppointments,
      "Treated": block.treatedAppointments,
      "Cancelled": block.cancelledAppointments,
      "No Shows": block.noShowAppointments,
      "Booked": block.bookedAppointments,
      "Progress %": `${block.progressPercentage}%`,
      "Start Date": formatDate(block.startDate, "yyyy-MM-dd"),
      "End Date": formatDate(block.endDate, "yyyy-MM-dd"),
      "Created Date": formatDate(block.createdAt, "yyyy-MM-dd"),
      "Created By": block.createdBy,
      "Has Next Block": block.hasNextBlock ? "Yes" : "No",
      "Has Previous Block": block.hasPreviousBlock ? "Yes" : "No",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Block Therapy Report");
    XLSX.writeFile(workbook, `Block_Therapy_Report_${formatDate(new Date(), "yyyy-MM-dd")}.xlsx`);
  };

  const columns = [
    {
      header: "Client Name",
      accessor: "clientName",
    },
    {
      header: "Clinic",
      accessor: "clinicName",
    },
    {
      header: "Clinician",
      accessor: "clinicianName",
    },
    {
      header: "Service",
      accessor: "serviceName",
    },
    {
      header: "Block Status",
      accessor: (row) => <StatusBadge status={row.blockStatus} />,
    },
    {
      header: "Progress",
      accessor: (row) => (
        <div className="flex items-center space-x-2">
          <div className="w-16">
            <ProgressBar percentage={row.progressPercentage} />
          </div>
          <span className="text-sm text-gray-600 w-12">
            {row.progressPercentage}%
          </span>
        </div>
      ),
    },
    {
      header: "Appointments",
      accessor: (row) => `${row.treatedAppointments}/${row.totalAppointments}`,
    },
    {
      header: "Service Price",
      accessor: (row) => formatCurrency(row.servicePrice),
    },
    {
      header: "Start Date",
      accessor: (row) => formatDate(row.startDate),
    },
    {
      header: "End Date",
      accessor: (row) => formatDate(row.endDate),
    },
    {
      header: "Created",
      accessor: (row) => formatDate(row.createdAt),
    },
  ];

  if (error) return <div className="text-red-500 p-4">Error: {error}</div>;

  return (
    <div className="space-y-6">
      {/* Filter Form */}
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-sm shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter Block Therapy Report</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label htmlFor="clinic" className="block text-sm font-medium text-gray-700 mb-1">
              Clinic
            </label>
            <select
              id="clinic"
              value={selectedClinic}
              onChange={(e) => setSelectedClinic(e.target.value)}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="all">All Clinics</option>
              {clinics && Array.isArray(clinics) ? clinics.map((clinic) => (
                <option key={clinic._id} value={clinic._id}>
                  {clinic.name}
                </option>
              )) : null}
            </select>
          </div>
          <div>
            <label htmlFor="clinician" className="block text-sm font-medium text-gray-700 mb-1">
              Clinician
            </label>
            <select
              id="clinician"
              value={selectedClinician}
              onChange={(e) => setSelectedClinician(e.target.value)}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="all">All Clinicians</option>
              {clinicians && Array.isArray(clinicians) ? clinicians.map((clinician) => (
                <option key={clinician._id} value={clinician._id}>
                  {clinician.firstName} {clinician.lastName}
                </option>
              )) : null}
            </select>
          </div>
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Block Status
            </label>
            <select
              id="status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active/In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Loading..." : "Filter Report"}
            </button>
          </div>
        </div>
      </form>

      {/* Show loading state */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading block therapy data...</p>
          </div>
        </div>
      )}

      {/* Show data only if we have it and not loading */}
      {!loading && summary && Object.keys(summary).length > 0 && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Total Blocks</h4>
              <p className="text-2xl font-bold text-gray-900">{summary.totalBlocks || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Active Blocks</h4>
              <p className="text-2xl font-bold text-blue-600">{summary.activeBlocks || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Completed Blocks</h4>
              <p className="text-2xl font-bold text-green-600">{summary.completedBlocks || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Cancelled Blocks</h4>
              <p className="text-2xl font-bold text-red-600">{summary.cancelledBlocks || 0}</p>
            </div>
          </div>

          {/* Additional Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Total Appointments</h4>
              <p className="text-xl font-semibold text-gray-900">{summary.totalAppointments || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Treated Appointments</h4>
              <p className="text-xl font-semibold text-green-600">{summary.totalTreatedAppointments || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Average Progress</h4>
              <p className="text-xl font-semibold text-blue-600">{summary.averageProgress || 0}%</p>
            </div>
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h4 className="text-sm font-medium text-gray-500">Unique Clients</h4>
              <p className="text-xl font-semibold text-purple-600">{summary.uniqueClients || 0}</p>
            </div>
          </div>

          {/* Revenue Card */}
          <div className="bg-white p-6 rounded-sm shadow-sm">
            <h4 className="text-sm font-medium text-gray-500">Total Revenue from Treated Appointments</h4>
            <p className="text-3xl font-bold text-green-600">{formatCurrency(summary.totalRevenue)}</p>
          </div>

          {/* Data Table */}
          <div className="bg-white shadow-sm rounded-sm">
            <div className="p-6">
              <Table
                columns={columns}
                data={blocks}
                title="Block Therapy Report"
                description="Comprehensive view of all therapy blocks, their progress, and status"
                actions={[
                  {
                    label: "Download Excel",
                    onClick: handleDownload,
                  },
                ]}
              />
            </div>
          </div>
        </>
      )}

      {/* Show message if no data and not loading */}
      {!loading && (!summary || Object.keys(summary).length === 0) && (
        <div className="bg-white p-6 rounded-sm shadow-sm text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Block Data Available</h3>
          <p className="text-gray-600">
            No therapy blocks found for the selected filters. Try adjusting your filter settings or check if there are any blocks in the system.
          </p>
        </div>
      )}
    </div>
  );
};

export default BlockTherapyReport;